import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import SettingServices from "../../../services/SettingServices";
import Loader from "../../../components/Loader";
import NoData from "../../../components/NoData";

const NOTE_TYPES = [
  "LOGIN_NOTES",
  "HOME_NOTES",
  "LIVE_SESSION_NOTE1",
  "LIVE_SESSION_NOTE2",
  "WINNING_SLIP_NOTE",
  "REGISTRATION",
];

const NOTE_LABELS = {
  LOGIN_NOTES: "Login notes",
  HOME_NOTES: "Home notes",
  LIVE_SESSION_NOTE1: "Live session note 1",
  LIVE_SESSION_NOTE2: "Live session note 2",
  WINNING_SLIP_NOTE: "Winning slip note",
  REGISTRATION: "Registration",
};

const GAME_ACTION_LABELS = {
  CREATE: "Created",
  UPDATE: "Edited",
  START: "Started",
  STOP: "Stopped",
  DELETE: "Deleted",
  BOARD: "Board",
};

const GAME_ACTION_CLASS = {
  CREATE: "is-create",
  UPDATE: "is-update",
  START: "is-start",
  STOP: "is-stop",
  DELETE: "is-delete",
  BOARD: "is-board",
};

const money = (field, v) =>
  v == null
    ? "—"
    : /amount/.test(field)
      ? `₹${v}`
      : field === "is_active"
        ? v
          ? "Live"
          : "Stopped"
        : String(v);

const WALLET_TYPES = {
  DEPOSIT_DISCOUNT: {
    label: "Deposit discount",
    enabled: "deposit_discount_enabled",
    fields: [
      ["deposit_discount_percentage", "Discount %", (v) => `${v}%`],
      ["deposit_discount_min_amount", "Min deposit", (v) => `₹${v}`],
    ],
  },
  WITHDRAWAL_FEE: {
    label: "Withdrawal fee",
    enabled: "withdrawal_fee_enabled",
    fields: [
      ["withdrawal_fee_percentage", "Fee %", (v) => `${v}%`],
      ["withdrawal_min_amount", "Min withdrawal", (v) => `₹${v}`],
    ],
  },
};

const NOTE_BADGE_CLASS = {
  HOME_NOTES: "home-notes",
  LOGIN_NOTES: "login-notes",
  WINNING_SLIP_NOTE: "winning-slip-notes",
  REGISTRATION: "login-notes",
};

/** Audit trail for settings changes. Fetches on mount and whenever a filter moves. */
export default function SettingsHistory({ refreshKey }) {
  const settingApi = new SettingServices();

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filter, setFilter] = useState(3);
  const [referenceTypeFilter, setReferenceTypeFilter] = useState("GAME");

  useEffect(() => {
    setLoading(true);
    settingApi
      .getSettingHistoryData(page, limit, filter, referenceTypeFilter || undefined)
      .then((response) => {
        if (response.isSuccess) {
          setData(response.data || []);
          setPagination(response.pagination || {});
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error get settings:", error);
        toast.dismiss();
        toast.error("Error get settings");
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filter, referenceTypeFilter, refreshKey]);

  return (
    <>
      <div className="settings-history-head d-flex align-items-center justify-content-between">
        <h5 className="settings-section-title">Settings History</h5>

        <div className="filter-bar history-filters">
          <select
            value={referenceTypeFilter}
            onChange={(e) => {
              setPage(1);
              setReferenceTypeFilter(e.target.value);
            }}
          >
            <option value="GAME">Games (create, edit, start/stop, board)</option>
            <option value="WALLET">Wallet (deposit discount, withdrawal fee)</option>
            <option value="GLOBAL">Global (legacy session settings)</option>
            <option value="NOTES">Note (all notes history)</option>
          </select>
          <select
            value={filter}
            onChange={(e) => {
              setPage(1);
              setFilter(e.target.value);
            }}
          >
            <option value={3}>Last 3 Days</option>
            <option value={7}>Last 7 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {loading ? (
        <Loader message="Loading history..." />
      ) : (
        <>
          <div className="table-responsive">
            <table className="settings-history-table">
              <thead>
                <tr>
                  <th>#</th>
                  {referenceTypeFilter === "GAME" ? (
                    <>
                      <th className="text-nowrap">Game</th>
                      <th className="text-nowrap">Action</th>
                      <th>Changes</th>
                      <th className="text-nowrap">By</th>
                    </>
                  ) : referenceTypeFilter === "WALLET" ? (
                    <>
                      <th className="text-nowrap">Setting</th>
                      <th className="text-nowrap">Status</th>
                      <th>Values</th>
                      <th className="text-nowrap">By</th>
                    </>
                  ) : referenceTypeFilter === "GLOBAL" ? (
                    <>
                      <th className="text-nowrap">Yantra</th>
                      <th className="text-nowrap">Winning</th>
                      <th className="text-nowrap">Difference</th>
                      <th className="text-nowrap">3 Min</th>
                      <th className="text-nowrap">5 Min</th>
                    </>
                  ) : referenceTypeFilter === "NOTES" ? (
                    <>
                      <th className="text-nowrap">Type</th>
                      <th>Details</th>
                    </>
                  ) : (
                    <>
                      <th className="text-nowrap">Type</th>
                      <th className="text-nowrap">Yantra</th>
                      <th className="text-nowrap">Winning</th>
                      <th className="text-nowrap">Difference</th>
                      <th className="text-nowrap">3 Min</th>
                      <th className="text-nowrap">5 Min</th>
                      <th>Details</th>
                    </>
                  )}
                  <th className="text-nowrap">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={10}>
                      <div className="d-flex align-items-center justify-content-center mb-5">
                        <NoData
                          message="No Settings History Found"
                          subText="No changes have been made yet. Update and save settings to view history."
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, i) => {
                    const refTypeRaw = row.reference_type
                      ? String(row.reference_type).toUpperCase()
                      : "GLOBAL";
                    const isNote = NOTE_TYPES.includes(refTypeRaw);
                    const refType = isNote ? refTypeRaw : "GLOBAL";

                    const dateStr = row.created_at
                      ? new Date(row.created_at).toLocaleString()
                      : "—";

                    if (WALLET_TYPES[refTypeRaw]) {
                      const meta = WALLET_TYPES[refTypeRaw];
                      let w = {};
                      try {
                        w =
                          typeof row.settings_data === "string"
                            ? JSON.parse(row.settings_data)
                            : row.settings_data || {};
                      } catch (_) {}
                      const on = Number(w[meta.enabled]) === 1;
                      const by = row.created_by_name || row.created_by_username || "—";
                      return (
                        <tr key={row.id}>
                          <td>{(page - 1) * limit + i + 1}</td>
                          <td className="text-nowrap">
                            <strong>{meta.label}</strong>
                          </td>
                          <td className={on ? "game-on_text" : "game-off_text"}>
                            {on ? "ACTIVE" : "INACTIVE"}
                          </td>
                          <td className="history-details-cell">
                            <div className="history-changes">
                              {meta.fields.map(([key, label, fmt]) => (
                                <span key={key} className="history-change-chip">
                                  <span className="history-change-label">{label}</span>
                                  <strong>{w[key] != null ? fmt(w[key]) : "—"}</strong>
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="text-nowrap">{by}</td>
                          <td className="text-nowrap">{dateStr}</td>
                        </tr>
                      );
                    }

                    if (refTypeRaw === "GAME") {
                      let g = {};
                      try {
                        g =
                          typeof row.settings_data === "string"
                            ? JSON.parse(row.settings_data)
                            : row.settings_data || {};
                      } catch (_) {}
                      const action = String(g.action || "").toUpperCase();
                      const changes = Array.isArray(g.changes) ? g.changes : [];
                      const by = row.created_by_name || row.created_by_username || "—";
                      return (
                        <tr key={row.id}>
                          <td>{(page - 1) * limit + i + 1}</td>
                          <td className="text-nowrap">
                            <strong>{g.game_name || g.game_code || "—"}</strong>
                            {g.game_code && (
                              <span className="history-game-code">{g.game_code}</span>
                            )}
                          </td>
                          <td className="text-nowrap">
                            <span
                              className={`history-type-badge history-game-action ${
                                GAME_ACTION_CLASS[action] || ""
                              }`}
                            >
                              {GAME_ACTION_LABELS[action] || action || "—"}
                            </span>
                          </td>
                          <td className="history-details-cell">
                            {changes.length === 0 && !g.note ? (
                              <span className="text-muted">—</span>
                            ) : (
                              <div className="history-changes">
                                {changes.map((c) => (
                                  <span key={c.field} className="history-change-chip">
                                    <span className="history-change-label">{c.label}</span>
                                    {action === "CREATE" ? (
                                      <strong>{money(c.field, c.to)}</strong>
                                    ) : (
                                      <>
                                        <s>{money(c.field, c.from)}</s>
                                        <span className="history-change-arrow">→</span>
                                        <strong>{money(c.field, c.to)}</strong>
                                      </>
                                    )}
                                  </span>
                                ))}
                                {g.note && (
                                  <span className="history-change-note">{g.note}</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="text-nowrap">{by}</td>
                          <td className="text-nowrap">{dateStr}</td>
                        </tr>
                      );
                    }

                    if (isNote) {
                      let noteText = "—";
                      try {
                        const parsed =
                          typeof row.settings_data === "string"
                            ? JSON.parse(row.settings_data)
                            : row.settings_data;
                        if (refType === "REGISTRATION" && parsed) {
                          noteText = `parent=${parsed.default_parent_id ?? "none"}, enabled=${parsed.registration_enabled ? "yes" : "no"}`;
                        } else if (parsed?.new_notes != null) {
                          noteText = String(parsed.new_notes);
                        } else if (parsed) {
                          noteText = String(parsed.new_notes);
                        }
                      } catch (_) {
                        noteText =
                          typeof row.settings_data === "string"
                            ? row.settings_data
                            : "—";
                      }
                      const excerpt =
                        noteText.length > 80
                          ? noteText.slice(0, 80) + "…"
                          : noteText;

                      return (
                        <tr key={row.id}>
                          <td>{(page - 1) * 10 + i + 1}</td>
                          <td>
                            <span
                              className={`history-type-badge ${
                                NOTE_BADGE_CLASS[refType] || "live-session-notes"
                              }`}
                            >
                              {NOTE_LABELS[refType] || refType}
                            </span>
                          </td>
                          <td className="history-details-cell" title={noteText}>
                            {excerpt}
                          </td>
                          <td className="text-nowrap">{dateStr}</td>
                        </tr>
                      );
                    }

                    let s = {};
                    try {
                      s =
                        typeof row.settings_data === "string"
                          ? JSON.parse(row.settings_data)
                          : row.settings_data || {};
                    } catch (_) {}

                    return (
                      <tr key={row.id}>
                        <td>{(page - 1) * 10 + i + 1}</td>
                        <td>{s.yantra_amount != null ? s.yantra_amount : "—"}</td>
                        <td>{s.winning_amount != null ? s.winning_amount : "—"}</td>
                        <td>{s.difference != null ? s.difference : "—"}</td>
                        {s.game_3min ? (
                          <td className="game-on_text">ON</td>
                        ) : (
                          <td className="game-off_text">OFF</td>
                        )}
                        {s.game_5min ? (
                          <td className="game-on_text">ON</td>
                        ) : (
                          <td className="game-off_text">OFF</td>
                        )}
                        <td className="text-nowrap">{dateStr}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="modern-pagination">
              <div className="mp-limit">
                <span>Show</span>
                <select value={limit} onChange={(e) => setLimit(e.target.value)}>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>

              <div className="mp-controls">
                <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                  Prev
                </button>

                <div className="mp-info">
                  Page <strong>{pagination.page}</strong> of{" "}
                  <strong>{pagination.totalPages}</strong>
                </div>

                <button
                  disabled={page === pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>

              <div className="mp-jump">
                <select value={page} onChange={(e) => setPage(e.target.value)}>
                  {Array.from({ length: pagination.totalPages }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      Page {i + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
